import { useState } from "react";
import { Loader2, Eye, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

/**
 * VisionAnalysisChip — Iter 429.p1
 *
 * Shows the GPT-5.6 Sol structured vision-analysis result for an image
 * attachment as a compact chip. Click to expand into the full report
 * (identified object, condition, faults with severity, recommendations).
 *
 * Consumes fields set by `routes/engineering._precompute_vision_analysis`
 * on the engineering_messages doc:
 *   • vision_analysis_status: running | ready | empty | error
 *   • vision_analysis: { identified_object, object_class, condition_summary,
 *                        faults[], recommendations[], confidence, notes, model }
 *
 * Wire the chip anywhere an image bubble is rendered. Shared across the
 * Engineering Desk and the Project Workbench brainstorm feed since both
 * store their attachments on engineering_messages.
 */

const _SEV_COLOR = {
  low: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  high: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export function VisionAnalysisChip({ msg, testIdPrefix = "vision-analysis" }) {
  const [open, setOpen] = useState(false);
  const status = msg?.vision_analysis_status;
  const a = msg?.vision_analysis;
  if (!status) return null;

  const testid = `${testIdPrefix}-${msg.id}`;

  if (status === "running") {
    return (
      <div
        className="mt-1 flex items-center gap-1 text-[10px] text-amber-300"
        data-testid={testid}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Analyzing image…</span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div
        className="mt-1 flex items-center gap-1 text-[10px] text-red-300"
        data-testid={testid}
      >
        <AlertTriangle className="w-3 h-3" />
        <span>Vision analysis failed</span>
      </div>
    );
  }
  if (status === "empty" || !a) {
    return (
      <div
        className="mt-1 flex items-center gap-1 text-[10px] text-[#a0aabf]"
        data-testid={testid}
      >
        <Eye className="w-3 h-3" />
        <span>Nothing recognisable detected</span>
      </div>
    );
  }

  // status === "ready"
  const faults = Array.isArray(a.faults) ? a.faults : [];
  const highSev = faults.some((f) => f.severity === "high");
  const chipColor = highSev
    ? "bg-rose-500/15 text-rose-200 border-rose-500/40"
    : faults.length > 0
      ? "bg-amber-500/15 text-amber-200 border-amber-500/40"
      : "bg-emerald-500/15 text-emerald-200 border-emerald-500/40";

  const short = a.identified_object || a.object_class || "Image";

  return (
    <div className="mt-1" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 text-[11px] px-1.5 py-0.5 rounded border ${chipColor} hover:brightness-110 transition`}
        data-testid={`${testid}-toggle`}
      >
        <Eye className="w-3 h-3" />
        <span className="max-w-[200px] truncate">{short}</span>
        {faults.length > 0 && (
          <span className="tabular-nums">
            · {faults.length} fault{faults.length === 1 ? "" : "s"}
          </span>
        )}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div
          className="mt-1 rounded border border-white/10 bg-black/30 p-2 text-[11px] text-[#c8d0e0] space-y-1.5 max-w-md"
          data-testid={`${testid}-details`}
        >
          {a.condition_summary && (
            <div>
              <span className="text-[#8a94ab]">Condition: </span>
              <span>{a.condition_summary}</span>
            </div>
          )}
          {faults.length > 0 && (
            <div className="space-y-1">
              <div className="text-[#8a94ab]">Faults observed:</div>
              {faults.map((f, i) => (
                <div key={i} className="flex flex-wrap items-start gap-1.5">
                  <span
                    className={`text-[9px] uppercase tracking-wide px-1 py-0.5 rounded border ${_SEV_COLOR[f.severity] || _SEV_COLOR.medium}`}
                  >
                    {f.severity}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-medium">{f.type}</span>
                    {f.location && <span className="text-[#8a94ab]"> @ {f.location}</span>}
                    {f.detail && <span> — {f.detail}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {faults.length === 0 && a.condition_summary && (
            <div className="text-emerald-300/80">No visible faults observed.</div>
          )}
          {Array.isArray(a.recommendations) && a.recommendations.length > 0 && (
            <div>
              <div className="text-[#8a94ab]">Recommended actions:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {a.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {a.notes && (
            <div className="text-[#8a94ab] italic">Note: {a.notes}</div>
          )}
          <div className="flex items-center gap-2 text-[9px] text-[#647087] pt-1 border-t border-white/5">
            {typeof a.confidence === "number" && (
              <span>Confidence {(a.confidence * 100).toFixed(0)}%</span>
            )}
            {a.model && <span>· {a.model}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default VisionAnalysisChip;
